/**
 * Pure utilities used by the trading mutation hooks — relocated verbatim
 * from frontend/src/queries/transactions.ts:121-516. None of these depend
 * on the host; they live in the SDK so the mutation hook bodies can stay
 * platform-neutral about toasts/telemetry/share but still call the same
 * helpers.
 */

import type { RecoveryAction, TxErrorInfo } from '../../../../tx-engine/index.js';
import { createTransactionTraceId } from '../../../../tx-engine/index.js';
import type { TxTranslator } from '../types.js';

// ── Sequential tx counter (per-direction, resets after 3s of inactivity) ────

let txCounter = 0;
let lastTxTimestamp = 0;
let lastTxDirection: 'buy' | 'sell' | 'swap' | null = null;
const TX_COUNTER_RESET_MS = 3_000;

export function nextTxNumber(direction: 'buy' | 'sell' | 'swap'): number {
  const now = Date.now();
  if (now - lastTxTimestamp > TX_COUNTER_RESET_MS || direction !== lastTxDirection) {
    txCounter = 0;
  }
  lastTxTimestamp = now;
  lastTxDirection = direction;
  txCounter += 1;
  return txCounter;
}

export function generateTraceId(): string {
  return createTransactionTraceId();
}

export function getToastId(base: string, traceId: string): string {
  return `${base}_${traceId}`;
}

export function formatTxLabel(txNum: number, formattedAmount: string): string {
  const numLabel = txNum > 1 ? ` ${String(txNum)}` : '';
  const amountLabel = formattedAmount !== '' ? ` (${formattedAmount})` : '';
  return `${numLabel}${amountLabel}`;
}

// ── Error categorization + message resolution ───────────────────────────────

export function categorizeRpcError(code: string): string {
  switch (code) {
    case 'TX_BLOCKHASH_EXPIRED':
      return 'blockhash_expired';
    case 'TX_SIM_FAILED':
    case 'TX_RELAY_SIM_FAILED':
      return 'simulation_failed';
    case 'TX_RELAY_FEE_PAYER_LOW':
      return 'insufficient_funds';
    case 'TX_CONFIRM_FAILED':
    case 'TX_RELAY_REJECTED':
      return 'program_error';
    default:
      return 'unknown';
  }
}

/**
 * Resolve a dotted i18n key like 'tx.errors.simFailed' using a next-intl
 * translator scoped to the 'tx' namespace. Falls back to the raw key if
 * translation is unavailable.
 */
export function resolveErrorMessage(userMessageKey: string, t: TxTranslator): string {
  // Keys are stored as 'tx.errors.simFailed'; the translator is scoped to
  // 'tx', so strip the 'tx.' prefix to get 'errors.simFailed'.
  const stripped = userMessageKey.startsWith('tx.') ? userMessageKey.slice(3) : userMessageKey;
  if (t.has(stripped)) {
    return t.format(stripped);
  }
  return userMessageKey;
}

export function getRecoveryDescription(
  action: RecoveryAction,
  t: TxTranslator,
): string | undefined {
  switch (action.type) {
    case 'prompt_retry':
      return t('recovery.tryAgain');
    case 'prompt_retry_with_higher_slippage':
      return t('recovery.retryWithSlippage', {
        slippage: String(action.suggestedSlippageBps / 100),
      });
    case 'auto_retry_after_delay':
      return t('recovery.retryingIn', { seconds: String(action.retryAfterSeconds) });
    case 'informational':
      return t('recovery.checkPortfolio');
    case 'not_retryable':
    case 'auto_retry':
      return undefined;
  }
}

// ── Base58 decode (for thesis tradeSignature → 64-byte signature) ───────────

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE58_MAP: Uint8Array = new Uint8Array(128);
for (let i = 0; i < BASE58_ALPHABET.length; i++) {
  BASE58_MAP[BASE58_ALPHABET.charCodeAt(i)] = i;
}

export function decodeBase58(input: string): Uint8Array {
  if (input.length === 0) return new Uint8Array(0);

  const bytes: number[] = [0];
  for (let i = 0; i < input.length; i++) {
    const charCode = input.charCodeAt(i);
    const value = charCode < 128 ? BASE58_MAP[charCode] : undefined;
    if (value === undefined) throw new Error(`Invalid base58 character: ${input[i] ?? ''}`);
    let carry = value;
    for (let j = 0; j < bytes.length; j++) {
      const byte = bytes[j];
      if (byte === undefined) continue;
      carry += byte * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }

  let leadingZeros = 0;
  for (let i = 0; i < input.length && input[i] === '1'; i++) {
    leadingZeros++;
  }

  const result = new Uint8Array(leadingZeros + bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[bytes.length - 1 - i];
    if (byte !== undefined) {
      result[leadingZeros + i] = byte;
    }
  }
  return result;
}

// Re-export for callers that need to suppress the `TxErrorInfo` import path.
export type { TxErrorInfo };
